import type { ActionInput, MatchEvent, MatchState } from "../game/simulation";
import type { ClientMessage, ServerMessage } from "../game/protocol";

export type ConnectionStatus = "idle" | "connecting" | "live" | "reconnecting" | "closed" | "error";

export interface MatchSocketUrlContext {
  reconnecting: boolean;
  attempt: number;
}

export type MatchSocketUrlProvider = (
  context: MatchSocketUrlContext
) => string | Promise<string>;

export interface MatchSocketOptions {
  url: string | MatchSocketUrlProvider;
  spectator?: boolean;
  onSnapshot: (state: MatchState, playerId?: string) => void;
  onEvents?: (events: MatchEvent[]) => void;
  onStatus?: (status: ConnectionStatus, latencyMs?: number) => void;
  onError?: (message: string) => void;
}

export class MatchSocket {
  private socket: WebSocket | null = null;
  private status: ConnectionStatus = "idle";
  private desiredOpen = false;
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private seq = 0;
  private lastInputAt = 0;
  private lastInputSignature = "";
  private playerId: string | undefined;
  private openGeneration = 0;

  constructor(private readonly options: MatchSocketOptions) {}

  connect(): void {
    if (this.desiredOpen) return;
    this.desiredOpen = true;
    void this.openSocket("connecting");
  }

  sendReady(): void {
    this.send({ type: "ready" });
  }

  pushInput(input: ActionInput): void {
    if (this.options.spectator || this.status !== "live") return;
    const now = performance.now();
    const signature = `${input.move}:${Number(input.jump)}:${Number(input.drop)}:${Number(input.fire)}`;
    if (signature === this.lastInputSignature && now - this.lastInputAt < 100) return;
    if (now - this.lastInputAt < 30) return;
    this.lastInputSignature = signature;
    this.lastInputAt = now;
    this.seq += 1;
    this.send({ type: "input", seq: this.seq, input });
  }

  reconnectNow(): void {
    if (!this.desiredOpen) return;
    this.clearTimers();
    this.openGeneration += 1;
    const previous = this.socket;
    this.socket = null;
    previous?.close(4000, "renewing session ticket");
    this.reconnectAttempt = 0;
    void this.openSocket("reconnecting");
  }

  close(): void {
    this.desiredOpen = false;
    this.openGeneration += 1;
    this.clearTimers();
    this.socket?.close(1000, "client closed");
    this.socket = null;
    this.setStatus("closed");
  }

  private async openSocket(nextStatus: "connecting" | "reconnecting"): Promise<void> {
    this.setStatus(nextStatus);
    const generation = ++this.openGeneration;
    let socketUrl: string;
    try {
      const source = this.options.url;
      socketUrl = typeof source === "string"
        ? source
        : await source({
            reconnecting: nextStatus === "reconnecting",
            attempt: this.reconnectAttempt
          });
    } catch (error) {
      if (generation !== this.openGeneration || !this.desiredOpen) return;
      this.handleConnectionFailure(error instanceof Error ? error.message : "경기 티켓을 갱신하지 못했습니다.");
      return;
    }

    if (generation !== this.openGeneration || !this.desiredOpen) return;
    let socket: WebSocket;
    try {
      socket = new WebSocket(socketUrl);
      this.socket = socket;
    } catch (error) {
      this.handleConnectionFailure(error instanceof Error ? error.message : "WebSocket connection failed");
      return;
    }
    socket.addEventListener("open", () => {
      if (this.socket !== socket) return;
      this.reconnectAttempt = 0;
      this.setStatus("live");
      if (!this.options.spectator) this.sendReady();
      this.startPing();
    });
    socket.addEventListener("message", (event) => {
      if (this.socket === socket) this.handleMessage(event.data);
    });
    socket.addEventListener("error", () => {
      if (this.socket === socket) this.options.onError?.("실시간 연결 오류가 발생했습니다.");
    });
    socket.addEventListener("close", () => {
      if (this.socket !== socket) return;
      this.clearPing();
      this.socket = null;
      if (this.desiredOpen) {
        this.scheduleReconnect();
      } else {
        this.setStatus("closed");
      }
    });
  }

  private handleMessage(data: unknown): void {
    if (typeof data !== "string") return;
    let message: ServerMessage;
    try {
      message = JSON.parse(data) as ServerMessage;
    } catch {
      this.options.onError?.("서버가 읽을 수 없는 메시지를 보냈습니다.");
      return;
    }

    switch (message.type) {
      case "welcome":
        this.playerId = message.playerId;
        this.options.onSnapshot(message.state, message.playerId);
        break;
      case "snapshot":
        this.options.onSnapshot(message.state, this.playerId);
        break;
      case "events":
        this.options.onEvents?.(message.events);
        break;
      case "pong":
        this.options.onStatus?.("live", Math.max(0, performance.now() - message.sentAt));
        break;
      case "error":
        this.options.onError?.(message.message);
        break;
    }
  }

  private send(message: ClientMessage): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  private scheduleReconnect(): void {
    if (!this.desiredOpen) return;
    this.reconnectAttempt += 1;
    this.setStatus("reconnecting");
    const delay = Math.min(5000, 300 * 2 ** Math.min(4, this.reconnectAttempt - 1));
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.openSocket("reconnecting");
    }, delay);
  }

  private handleConnectionFailure(message: string): void {
    this.options.onError?.(message);
    if (this.desiredOpen) this.scheduleReconnect();
  }

  private startPing(): void {
    this.clearPing();
    this.pingTimer = window.setInterval(() => {
      this.send({ type: "ping", sentAt: performance.now() });
    }, 5000);
  }

  private clearPing(): void {
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private clearTimers(): void {
    this.clearPing();
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private setStatus(status: ConnectionStatus): void {
    this.status = status;
    this.options.onStatus?.(status);
  }
}

export function playerSocketUrl(matchId: string, ticket: string): string {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const url = new URL(`${protocol}//${location.host}/ws/matches/${encodeURIComponent(matchId)}`);
  url.searchParams.set("ticket", ticket);
  return url.toString();
}

export function spectatorSocketUrl(matchId: string): string {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const url = new URL(`${protocol}//${location.host}/ws/matches/${encodeURIComponent(matchId)}`);
  url.searchParams.set("role", "spectator");
  if (isLocalDevelopmentHost(location.hostname)) {
    url.searchParams.set("devAdmin", "1");
  }
  return url.toString();
}

function isLocalDevelopmentHost(hostname: string): boolean {
  return hostname === "127.0.0.1" ||
    hostname === "localhost" ||
    hostname.startsWith("192.168.") ||
    hostname.startsWith("10.") ||
    /^172\.(?:1[6-9]|2\d|3[01])\./u.test(hostname);
}
